import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useSuggestedPrice } from '#/features/market-rate'
import type { MarketRateRow } from '#/features/market-rate'
import {
  useConvertShipmentApiV1ShipmentsShipmentIdConvertPost,
  useUpdateShipmentApiV1ShipmentsShipmentIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'
import { invalidateShipmentLists } from './invalidate-shipment-queries'
import { SHIPMENT_STAGES } from '../model/stages'
import type { ShipmentStage } from '../model/stages'

interface UseShipmentTariffEditorOptions {
  shipmentId: string
  deposit?: number
  /**
   * When set, saving the tariff calls `POST /shipments/{id}/convert` with
   * stage+status+tariff+deposit in a single request, advancing the shipment
   * to this stage (used on the leads table where editing the tariff promotes
   * the lead to a quote). Otherwise a plain tariff PATCH is sent.
   */
  convertToStage?: ShipmentStage
}

function useTariffMutations() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Invalidate the lists AND the stage counts — a bare `['/api/v1/shipments']`
  // key misses `['/api/v1/shipments/counts']`, so the sidebar badges would go
  // stale after a convert/tariff change.
  const invalidateShipments = () => invalidateShipmentLists(queryClient)

  const convertShipment = useConvertShipmentApiV1ShipmentsShipmentIdConvertPost(
    {
      mutation: {
        onSuccess: (response, variables) => {
          invalidateShipments()
          if (variables.data.stage !== SHIPMENT_STAGES.QUOTE) return
          const detail = response.data
          toast.success(
            t('table.tariff.convertedToQuote', { code: detail.code }),
            {
              action: {
                label: t('table.tariff.viewQuote'),
                onClick: () => navigate({ to: `/quotes/${detail.id}` }),
              },
            },
          )
        },
        onError: (error) => {
          toast.error(t('table.tariff.activateError'), {
            description: getApiErrorDetail(error) ?? undefined,
          })
        },
      },
    },
  )

  const updateShipment = useUpdateShipmentApiV1ShipmentsShipmentIdPatch({
    mutation: {
      onSuccess: () => {
        invalidateShipments()
        toast.success(t('table.tariff.updateSuccess'))
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? t('table.tariff.updateError'))
      },
    },
  })

  return { convertShipment, updateShipment }
}

export interface ShipmentTariffEditor {
  /** Persists the value (convert-to-stage or plain tariff PATCH). */
  save: (value: number) => void
  isPending: boolean
  /** Set when a market rate is chosen, so inputs can re-seed from it. */
  seededPrice?: number
  /** Drop a price into the editor's input without persisting it. */
  seedPrice: (value: number) => void
  marketRate: {
    open: boolean
    setOpen: (open: boolean) => void
    stats: ReturnType<typeof useSuggestedPrice>['stats']
    rows: ReturnType<typeof useSuggestedPrice>['rows']
    isLoading: boolean
    errorMessage?: string
    onUseRate: (row: MarketRateRow) => void
  }
}

/**
 * Shared tariff-editing behaviour for the leads/quotes/orders surfaces — the
 * desktop `ShipmentTariffCell` and the mobile shipment card both drive their
 * inputs from this hook so the convert/update + market-rate flow lives once.
 */
export function useShipmentTariffEditor({
  shipmentId,
  deposit,
  convertToStage,
}: UseShipmentTariffEditorOptions): ShipmentTariffEditor {
  const [open, setOpen] = useState(false)
  const [seededPrice, setSeededPrice] = useState<number | undefined>()
  const suggestion = useSuggestedPrice({ shipmentId, enabled: open })
  const { convertShipment, updateShipment } = useTariffMutations()

  const save = (value: number) => {
    if (convertToStage) {
      convertShipment.mutate({
        shipmentId,
        data: {
          stage: convertToStage,
          status: convertToStage,
          tariff: value,
          deposit,
        },
      })
      return
    }
    updateShipment.mutate({ shipmentId, data: { tariff: value } })
  }

  return {
    save,
    isPending: updateShipment.isPending || convertShipment.isPending,
    seededPrice,
    seedPrice: setSeededPrice,
    marketRate: {
      open,
      setOpen,
      stats: suggestion.stats,
      rows: suggestion.rows,
      isLoading: suggestion.isLoading,
      errorMessage: suggestion.errorMessage,
      onUseRate: (row) => setSeededPrice(row.actualPrice ?? row.listedPrice),
    },
  }
}
